import React, { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Check, X, ChevronDown, Inbox, Mail } from "lucide-react";
import { FIELD_LABELS, GROUPS } from "@/lib/reteForm";

const fmt = (iso) => new Date(iso).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" });
const groupLabel = (k) => GROUPS.find((g) => g.key === k)?.label || k;

function show(key, v) {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ") || "—";
  if (typeof v === "boolean") return v ? "Sì" : "No";
  if (key === "friendly") return Object.entries(v).filter(([, x]) => x).map(([k]) => groupLabel(k)).join(", ") || "nessuno";
  if (key === "formed") return Object.entries(v).filter(([, x]) => x && x !== "no").map(([k, x]) => `${groupLabel(k)}: ${x}`).join(" · ") || "nessuno";
  return String(v);
}

function groupDiff(key, now, before) {
  const val = (obj, g) => (key === "friendly" ? (obj?.[g] ? "Sì" : "No") : obj?.[g] || "no");
  return GROUPS.filter((g) => val(now, g.key) !== val(before, g.key))
    .map((g) => `${g.label}: ${val(before, g.key)} → ${val(now, g.key)}`).join(" · ");
}

function RequestCard({ req, onAction }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const d = req.data;
  const changed = req.current ? Object.keys(FIELD_LABELS).filter((k) => show(k, d[k]) !== show(k, req.current[k])) : [];
  const act = async (kind) => {
    if (kind === "reject" && !window.confirm("Rifiutare questa richiesta?")) return;
    setBusy(true);
    try {
      const r = await http.post(`/rete/requests/${req.id}/${kind}`);
      if (kind === "approve" && r.data.email_sent === false) alert("Scheda pubblicata, ma l'email di conferma non è partita. Puoi avvisare la persona a mano.");
      onAction();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
      setBusy(false);
    }
  };
  return (
    <li className="rounded-2xl border border-viola/15 bg-white/80 p-4" data-testid={`rete-request-${req.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-semibold">{d.nome} {d.cognome}
            <span className={`ml-2 align-middle rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${req.type === "update" ? "bg-giallo text-viola" : "bg-lilla text-viola"}`}>{req.type === "update" ? "Aggiornamento" : "Nuova scheda"}</span>
          </p>
          <p className="text-xs text-viola/60">{fmt(req.created_at)} · {d.qualifica.join(", ")} · {d.city}</p>
          {req.type === "update" && <p className="text-xs text-viola/70 mt-1">{changed.length ? `Cambia: ${changed.map((k) => FIELD_LABELS[k]).join(", ")}` : "Nessuna differenza rispetto alla scheda attuale"}</p>}
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="inline-flex items-center gap-1 rounded-full border border-viola/20 px-3 py-1.5 text-xs font-semibold" data-testid={`rete-request-details-${req.id}`}>
            Dettagli <ChevronDown size={12} className={open ? "rotate-180" : ""} aria-hidden />
          </button>
          <button type="button" disabled={busy} onClick={() => act("approve")} className="inline-flex items-center gap-1 rounded-full bg-viola text-cream px-3 py-1.5 text-xs font-semibold disabled:opacity-60" data-testid={`rete-request-approve-${req.id}`}>
            <Check size={12} aria-hidden /> Approva
          </button>
          <button type="button" disabled={busy} onClick={() => act("reject")} className="inline-flex items-center gap-1 rounded-full border border-red-600 text-red-700 px-3 py-1.5 text-xs font-semibold disabled:opacity-60" data-testid={`rete-request-reject-${req.id}`}>
            <X size={12} aria-hidden /> Rifiuta
          </button>
        </div>
      </div>
      {open && (
        <dl className="mt-4 grid gap-2 text-sm">
          {Object.keys(FIELD_LABELS).map((k) => {
            const isChanged = changed.includes(k);
            return (
              <div key={k} className={`grid sm:grid-cols-[180px_1fr] gap-1 rounded-lg px-2 py-1 ${isChanged ? "bg-giallo/30" : ""}`}>
                <dt className="font-semibold">{FIELD_LABELS[k]}</dt>
                <dd className="break-words">
                  {isChanged && (k === "friendly" || k === "formed")
                    ? <span className="block font-semibold">Cambia: {groupDiff(k, d[k], req.current[k])}</span>
                    : show(k, d[k])}
                  {isChanged && k !== "friendly" && k !== "formed" && <span className="block text-xs text-viola/60">Prima: {show(k, req.current[k])}</span>}
                </dd>
              </div>
            );
          })}
        </dl>
      )}
    </li>
  );
}

export function ReteRequestsPanel({ onChanged }) {
  const [reqs, setReqs] = useState([]);
  const [mail, setMail] = useState(null);
  const [sending, setSending] = useState(false);
  const [recent, setRecent] = useState([]);
  const load = () => {
    http.get("/rete/requests").then((r) => setReqs(r.data)).catch(() => {});
    http.get("/rete/requests?status=auto_approved").then((r) => setRecent(r.data)).catch(() => {});
  };
  const loadMail = () => http.get("/rete/digest-log").then((r) => setMail(r.data[0] || null)).catch(() => {});
  useEffect(() => { load(); loadMail(); }, []);
  const after = () => { load(); onChanged?.(); };
  const sendNow = async () => {
    setSending(true);
    try { const r = await http.post("/rete/digest-now"); setMail(r.data); } catch { setMail({ status: "error", error: "Richiesta non riuscita", at: new Date().toISOString() }); }
    setSending(false);
  };
  const mailText = !mail ? "Nessun riepilogo inviato finora."
    : mail.status === "sent" ? `Ultimo riepilogo inviato il ${fmt(mail.at)} (${mail.pending} in attesa${mail.published ? `, ${mail.published} aggiornate` : ""}).`
    : mail.status === "skipped" ? `Ultimo controllo il ${fmt(mail.at)}: niente di nuovo, nessuna email inviata.`
    : `Ultimo invio non riuscito il ${fmt(mail.at)}: ${mail.error}`;
  return (
    <div className="rounded-2xl bg-white/60 border border-viola/15 p-5" data-testid="rete-requests-panel">
      <p className="font-display text-lg flex items-center gap-2"><Inbox size={18} aria-hidden /> Richieste dal sito {reqs.length > 0 && <span className="rounded-full bg-fucsia text-white text-xs px-2 py-0.5" data-testid="rete-requests-count">{reqs.length}</span>}</p>
      <p className="text-sm text-viola/70 mt-1">Quando approvi, la scheda compare subito nella Rete e la persona riceve un'email con il link alla sua pagina. Le modifiche fatte dalle persone con il loro link personale vengono pubblicate subito e le trovi nel riepilogo del mattino. Se poi modifichi quella persona nel foglio, vince il foglio (la risposta su sostanze resta).</p>
      {reqs.length ? (
        <ul className="mt-4 space-y-3">{reqs.map((r) => <RequestCard key={r.id} req={r} onAction={after} />)}</ul>
      ) : (
        <p className="mt-4 text-sm text-viola/60" data-testid="rete-requests-empty">Nessuna richiesta in attesa.</p>
      )}
      {recent.length > 0 && (
        <details className="mt-4 text-sm" data-testid="rete-recent-updates">
          <summary className="cursor-pointer font-semibold">Ultime schede aggiornate dalle persone ({recent.length})</summary>
          <ul className="mt-2 space-y-1">
            {recent.slice(0, 15).map((r) => (
              <li key={r.id} className="flex flex-wrap justify-between gap-2">
                <a href={`/rete/scheda/${r.target_id}`} target="_blank" rel="noreferrer" className="underline">{r.data.nome} {r.data.cognome}</a>
                <span className="text-viola/60">{fmt(r.handled_at || r.created_at)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      <div className="mt-4 pt-4 border-t border-viola/10 flex flex-wrap items-center justify-between gap-3 text-xs">
        <p className={mail?.status === "error" ? "text-red-700" : "text-viola/70"} data-testid="rete-digest-status">
          <Mail size={12} className="inline mr-1 -mt-0.5" aria-hidden />Ogni mattina alle 8 ti arriva un riepilogo su danpiras.psy@gmail.com, solo se ci sono richieste in attesa o schede aggiornate. {mailText}
        </p>
        <button type="button" onClick={sendNow} disabled={sending} className="rounded-full border border-viola/25 px-3 py-1.5 font-semibold disabled:opacity-60" data-testid="rete-digest-now">
          {sending ? "Invio…" : "Invia ora il riepilogo"}
        </button>
      </div>
    </div>
  );
}
